import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faMinus,
  faPlus,
  faPuzzlePiece,
  faGamepad,
} from '@fortawesome/free-solid-svg-icons';
import Alert from '../admincomponents/components/Alert';
import Loading from '../admincomponents/components/Loading';
import { createSketcherGame } from '../../apiservices/axiosservice/gameservice';
import { getSubscriber } from '../../apiservices/LocalStorageService';
import SketcherPublish from './SketcherPublish';
import './SketcherSetup.css';

const SketcherSetup: React.FC = () => {
  const navigate = useNavigate();
  const [totalSketch, setTotalSketch] = useState(4);
  const [timeAllowed, setTimeAllowed] = useState(2);
  const [gameCapacity, setGameCapacity] = useState(20);
  const [scheduledGame, setScheduledGame] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const [publishedGame, setPublishedGame] = useState<CreateSketcherGameResponse['data'] | null>(null);

  const clamp = (val: number, min: number, max: number) =>
    Math.max(min, Math.min(max, val));

  const handlePublish = async () => {
    const subscriber = getSubscriber();
    if (!subscriber?.userId) {
      setAlertMsg('User session not found');
      return;
    }
    setLoading(true);
    try {
      const payload: CreateSketcherGameRequest = {
        totalSketch,
        gameOwnerId: subscriber.userId,
        timeAllowed: timeAllowed * 60,
        scheduledGame,
        gameType: 'sketcher',
        gameCapacity,
      };
      const res = await createSketcherGame(payload);
      setPublishedGame(res.data);
    } catch (err: any) {
      setAlertMsg(err.message || 'Failed to create sketcher game');
    } finally {
      setLoading(false);
    }
  };

  if (publishedGame) {
    return (
      <SketcherPublish
        gameId={publishedGame.gameId}
        gameTitle={publishedGame.gameTitle}
        totalSketch={totalSketch}
        timeAllowed={timeAllowed}
        gameCapacity={gameCapacity}
      />
    );
  }

  return (
    <div className="sketcher-setup">
      {/* Header */}
      <div className="sketcher-setup__header">
        <button
          className="sketcher-setup__back"
          onClick={() => navigate('/dashboard/games')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h1 className="sketcher-setup__title">Sketcher Game Setup</h1>
      </div>

      <div className="sketcher-setup__breadcrumb">
        <span className="sketcher-setup__crumb">Game Type</span>
        <span className="sketcher-setup__crumb-sep">/</span>
        <span className="sketcher-setup__crumb sketcher-setup__crumb--active">
          Sketcher
        </span>
      </div>

      <div className="sketcher-setup__body">
        {/* Main content */}
        <div className="sketcher-setup__main">
          {/* Round Settings */}
          <div className="sketcher-setup__card">
            <h3 className="sketcher-setup__card-title">Game Settings</h3>

            <label className="sketcher-setup__label">Number of Sketches</label>
            <div className="sketcher-setup__counter">
              <button
                className="sketcher-setup__counter-btn"
                onClick={() => setTotalSketch((r) => clamp(r - 1, 1, 20))}
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
              <span className="sketcher-setup__counter-value">{totalSketch}</span>
              <button
                className="sketcher-setup__counter-btn"
                onClick={() => setTotalSketch((r) => clamp(r + 1, 1, 20))}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>

            <label className="sketcher-setup__label">Time per Sketch</label>
            <div className="sketcher-setup__counter">
              <button
                className="sketcher-setup__counter-btn"
                onClick={() => setTimeAllowed((t) => clamp(t - 1, 1, 10))}
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
              <span className="sketcher-setup__counter-value">
                {timeAllowed} min{timeAllowed > 1 ? 's' : ''}
              </span>
              <button
                className="sketcher-setup__counter-btn"
                onClick={() => setTimeAllowed((t) => clamp(t + 1, 1, 10))}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>

            <label className="sketcher-setup__label">Game Capacity</label>
            <div className="sketcher-setup__counter">
              <button
                className="sketcher-setup__counter-btn"
                onClick={() => setGameCapacity((c) => clamp(c - 5, 5, 100))}
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
              <span className="sketcher-setup__counter-value">
                {gameCapacity} players
              </span>
              <button
                className="sketcher-setup__counter-btn"
                onClick={() => setGameCapacity((c) => clamp(c + 5, 5, 100))}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>

            <div className="sketcher-setup__toggle-item">
              <FontAwesomeIcon
                icon={faGamepad}
                className="sketcher-setup__toggle-icon"
              />
              <span>Scheduled Game</span>
              <label className="sketcher-setup__toggle">
                <input
                  type="checkbox"
                  checked={scheduledGame}
                  onChange={(e) => setScheduledGame(e.target.checked)}
                />
                <span className="sketcher-setup__toggle-slider" />
              </label>
            </div>
          </div>
        </div>

        {/* Right panel: Game Preview */}
        <div className="sketcher-setup__preview">
          <div className="sketcher-setup__card">
            <h3 className="sketcher-setup__card-title">Game Preview</h3>
            <div className="sketcher-setup__preview-icon">
              <FontAwesomeIcon icon={faPuzzlePiece} />
            </div>

            <div className="sketcher-setup__summary">
              <h4 className="sketcher-setup__summary-title">
                🎨 Configuration Summary
              </h4>
              <div className="sketcher-setup__summary-row">
                <span>Sketches:</span>
                <span className="sketcher-setup__summary-val">{totalSketch}</span>
              </div>
              <div className="sketcher-setup__summary-row">
                <span>Time Limit:</span>
                <span className="sketcher-setup__summary-val">
                  {timeAllowed} min{timeAllowed > 1 ? 's' : ''}
                </span>
              </div>
              <div className="sketcher-setup__summary-row">
                <span>Capacity:</span>
                <span className="sketcher-setup__summary-val">
                  {gameCapacity} players
                </span>
              </div>
              <div className="sketcher-setup__summary-row">
                <span>Mode:</span>
                <span className="sketcher-setup__summary-badge">
                  {scheduledGame ? 'Scheduled' : 'Live'}
                </span>
              </div>
            </div>

            <button className="sketcher-setup__publish-btn" onClick={handlePublish}>
              Create Game
            </button>
          </div>
        </div>
      </div>

      {loading && <Loading message="Creating Sketcher Game..." />}
      {alertMsg && (
        <Alert
          title="Notice"
          isOpen={!!alertMsg}
          cancelText="Ok"
          message={alertMsg}
          onCancel={() => setAlertMsg('')}
        />
      )}
    </div>
  );
};

export default SketcherSetup;
